// Iter 204a — Compact Asian Handicap card for the Match Details modal.
//
// Reads the `expected_goals` already produced by the prediction engine
// in the parent modal, hits /api/asian-handicap/analyze (no auth — pure
// math), and surfaces:
//   • xGD + Monte Carlo verification
//   • Fair handicap line
//   • Margin distribution sparkline
//   • Best value bet OR "NO BET — MARKET EFFICIENT"
//
// The user can paste their bookmaker's lines inline to score edges
// without leaving the match modal.

import React, { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Target } from "lucide-react";
import { API } from "../App";

const TIER_TONE = {
  "A+": "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  "A":  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "B+": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "B":  "bg-amber-500/10 text-amber-200 border-amber-500/20",
  "C":  "bg-slate-500/15 text-slate-300 border-slate-500/30",
  "NO BET": "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const MARGIN_LABELS = {
  lose_by_2_plus: "Away 2+", lose_by_1: "Away 1",
  draw: "Draw",
  win_by_1: "Home 1", win_by_2: "Home 2", win_by_3_plus: "Home 3+",
};

const DEFAULT_LINES = [
  { line: "-1.0", home_odds: "", away_odds: "" },
  { line: "-0.5", home_odds: "", away_odds: "" },
  { line: "0", home_odds: "", away_odds: "" },
  { line: "0.5", home_odds: "", away_odds: "" },
  { line: "1.0", home_odds: "", away_odds: "" },
];


export default function AsianHandicapInlineCard({ homeTeam, awayTeam, league, expectedGoals, fixtureId }) {
  const [marketLines, setMarketLines] = useState(DEFAULT_LINES);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lambdaHome = Number(expectedGoals?.home || 1.4);
  const lambdaAway = Number(expectedGoals?.away || 1.2);
  const hasMarketLines = marketLines.some((r) => r.home_odds && r.away_odds);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const market_lines = Object.fromEntries(
        marketLines
          .filter((r) => r.home_odds && r.away_odds)
          .map((r) => [String(parseFloat(r.line)),
            { home_odds: parseFloat(r.home_odds), away_odds: parseFloat(r.away_odds) }]),
      );
      const res = await axios.post(`${API}/asian-handicap/analyze`, {
        home_team: homeTeam || "Home",
        away_team: awayTeam || "Away",
        league: league || "Unknown",
        lambda_home: lambdaHome, lambda_away: lambdaAway,
        rho: 0.10,
        market_lines,
        mc_iterations: 3000,
        fixture_id: fixtureId,
      });
      setAnalysis(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Analyse failed");
    } finally { setLoading(false); }
  }, [homeTeam, awayTeam, league, lambdaHome, lambdaAway, marketLines]);

  const setLine = (i, k, v) =>
    setMarketLines((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  // Auto-run once on first mount so users see xGD + fair line before
  // they paste any market odds.  Pulling the request into the effect
  // (rather than calling `run()`) keeps the dependency list clean and
  // avoids re-issuing the request on every keystroke.
  React.useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/asian-handicap/analyze`, {
          home_team: homeTeam || "Home",
          away_team: awayTeam || "Away",
          league: league || "Unknown",
          lambda_home: lambdaHome, lambda_away: lambdaAway,
          rho: 0.10,
          market_lines: {},
          mc_iterations: 3000,
          fixture_id: fixtureId,
        });
        if (!cancel) setAnalysis(res.data);
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.detail || e.message || "Analyse failed");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [homeTeam, awayTeam, league, lambdaHome, lambdaAway]);

  const tone = TIER_TONE[analysis?.risk_tier] || TIER_TONE.C;
  const topLines = useMemo(() => {
    if (!analysis) return [];
    return [...analysis.line_evaluations]
      .filter((e) => e.edge_pct != null)
      .sort((a, b) => (b.edge_pct ?? -999) - (a.edge_pct ?? -999))
      .slice(0, 4);
  }, [analysis]);

  return (
    <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 rounded-xl p-6"
         data-testid="modal-ah-card">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-6 h-6 text-indigo-300" />
        <h3 className="text-2xl font-bold text-white">Asian Handicap Value</h3>
        <span className="text-[10px] uppercase tracking-wide text-indigo-200/70 ml-2">
          xGD · 50k MC · Edge
        </span>
      </div>

      {error && <div className="text-rose-300 text-sm mb-2" data-testid="modal-ah-error">{error}</div>}

      {analysis && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className={`rounded-lg border p-3 ${tone}`} data-testid="modal-ah-tier">
            <div className="text-[10px] uppercase tracking-wide opacity-80">Verdict</div>
            <div className="text-2xl font-bold mt-0.5">{analysis.risk_tier}</div>
            {analysis.best_bet ? (
              <div className="text-xs mt-1 leading-snug">
                <span className="font-semibold">
                  {analysis.best_bet.side === "home" ? analysis.home_team : analysis.away_team}
                </span>{" "}AH {analysis.best_bet.line >= 0 ? `+${analysis.best_bet.line}` : analysis.best_bet.line}
                {" "}@ {analysis.best_bet.market_odds}
                <div className="font-semibold text-base mt-0.5">+{analysis.best_bet.edge_pct.toFixed(2)}% edge</div>
              </div>
            ) : (
              <div className="text-xs mt-1 opacity-90" data-testid="modal-ah-no-bet">
                {analysis.no_bet_reason || "NO BET — market efficient."}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Expected Goals</div>
            <div className="text-sm text-white mt-1">
              {analysis.lambda_home} <span className="text-slate-500">vs</span> {analysis.lambda_away}
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-slate-300">
              <div>xGD: <strong className="text-white">{analysis.expected_goal_difference}</strong></div>
              <div>MC xGD: {analysis.monte_carlo_xgd}</div>
              <div>Fair line:{" "}
                <strong className="text-white">
                  {analysis.fair_handicap_line >= 0
                    ? `+${analysis.fair_handicap_line}`
                    : analysis.fair_handicap_line}
                </strong></div>
              <div className="text-slate-400">Confidence: {analysis.confidence_pct}%</div>
            </div>
          </div>

          <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Margin distribution</div>
            <div className="space-y-1">
              {Object.entries(analysis.margin_probabilities).map(([k, pct]) => (
                <div key={k} className="flex items-center gap-1.5 text-[11px]">
                  <span className="w-14 text-right text-slate-400">{MARGIN_LABELS[k] || k}</span>
                  <span className="flex-1 h-2 bg-slate-700 rounded overflow-hidden">
                    <span className="block h-full bg-indigo-400"
                          style={{ width: `${Math.min(100, pct * 2)}%` }} />
                  </span>
                  <span className="w-10 text-right font-mono text-slate-300">{pct.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-3">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Bookmaker AH lines (paste odds to score)</div>
          <button onClick={run} disabled={loading}
                  data-testid="modal-ah-rerun"
                  className="text-xs text-indigo-300 hover:text-indigo-200 disabled:opacity-50">
            {loading ? "Analysing…" : hasMarketLines ? "Score lines" : "Re-run"}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {marketLines.map((r, i) => (
            <div key={i} className="flex gap-1.5 items-center bg-slate-900/40 rounded px-2 py-1">
              <input className="bg-transparent text-white text-xs w-12 outline-none"
                     value={r.line} onChange={(e) => setLine(i, "line", e.target.value)}
                     data-testid={`modal-ah-line-${i}`} />
              <input className="bg-transparent text-white text-xs w-14 outline-none border-l border-slate-700 pl-1.5"
                     placeholder="home" type="number" step="0.01"
                     value={r.home_odds} onChange={(e) => setLine(i, "home_odds", e.target.value)}
                     data-testid={`modal-ah-home-${i}`} />
              <input className="bg-transparent text-white text-xs w-14 outline-none border-l border-slate-700 pl-1.5"
                     placeholder="away" type="number" step="0.01"
                     value={r.away_odds} onChange={(e) => setLine(i, "away_odds", e.target.value)}
                     data-testid={`modal-ah-away-${i}`} />
            </div>
          ))}
        </div>
      </div>

      {analysis && topLines.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Top edges</div>
          <div className="space-y-1">
            {topLines.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-xs px-3 py-1.5 rounded bg-slate-900/40"
                   data-testid={`modal-ah-top-${i}`}>
                <span className="text-slate-300">
                  <span className="font-mono">{e.line >= 0 ? `+${e.line}` : e.line}</span>{" "}
                  <span className="capitalize">{e.side}</span>{" "}
                  @ {e.market_odds}
                </span>
                <span className={
                  e.edge_pct >= 3 ? "text-emerald-300 font-semibold" :
                  e.edge_pct >= 0 ? "text-slate-300" : "text-rose-400"
                }>
                  {e.edge_pct >= 0 ? "+" : ""}{e.edge_pct.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
